import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { BookOpen, Briefcase, ArrowRight, GraduationCap, FileText, Award, Sparkles, X, Plus } from "lucide-react";

export default function StudentDashboard() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ courses: 0, jobs: 0 });
  const [skills, setSkills] = useState(user?.skills || []);
  const [skillInput, setSkillInput] = useState("");
  const [savingSkills, setSavingSkills] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/public/courses").then(({ data }) => data.length).catch(() => 0),
      api.get("/jobs").then(({ data }) => data.length).catch(() => 0),
    ]).then(([courses, jobs]) => setStats({ courses, jobs }));
  }, []);

  const addSkill = () => {
    const v = skillInput.trim();
    if (v && !skills.some((s) => s.toLowerCase() === v.toLowerCase())) {
      setSkills([...skills, v]);
    }
    setSkillInput("");
  };
  const removeSkill = (s) => setSkills(skills.filter((x) => x !== s));
  const saveSkills = async () => {
    setSavingSkills(true);
    try {
      const { data } = await api.put("/student/profile", { skills });
      setUser?.(data);
      setSkills(data.skills || []);
      toast.success(t("studentDash.skillsSaved"));
    } catch (e) {
      toast.error(t("studentDash.skillsError"));
    } finally {
      setSavingSkills(false);
    }
  };

  const menus = [
    {
      testid: "menu-join-course", icon: GraduationCap, action: () => navigate("/courses"),
      title: t("studentDash.joinTitle"), desc: t("studentDash.joinDesc"),
      badge: t("studentDash.joinBadge"), accent: "from-amber-500 to-orange-600",
      stat: `${stats.courses}+ ${t("common.courses")}`,
    },
    {
      testid: "menu-apply-job", icon: Briefcase, action: () => navigate("/jobs"),
      title: t("studentDash.applyTitle"), desc: t("studentDash.applyDesc"),
      badge: t("studentDash.applyBadge"), accent: "from-indigo-600 to-indigo-900",
      stat: `${stats.jobs}+ ${t("common.openings")}`,
    },
  ];

  const quick = [
    { icon: BookOpen, label: t("nav.myLearning"), to: "/my-learning", testid: "quick-learning" },
    { icon: FileText, label: t("nav.myApplications"), to: "/my-applications", testid: "quick-applications" },
    { icon: Award, label: t("nav.courses"), to: "/courses", testid: "quick-courses" },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF9]" data-testid="student-dashboard">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
        <div className="mb-10">
          <p className="text-base font-semibold uppercase tracking-wider text-amber-600">{t("studentDash.eyebrow")}</p>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            {t("studentDash.hello", { name: user?.name?.split(" ")[0] || "" })}
          </h1>
          <p className="mt-3 text-lg text-slate-500">{t("studentDash.subtitle")}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {menus.map((m) => (
            <button
              key={m.testid}
              onClick={m.action}
              data-testid={m.testid}
              className="group text-left rounded-3xl border-2 border-slate-200 bg-white p-8 transition-all hover:-translate-y-1 hover:border-amber-400 hover:shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <div className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${m.accent} flex items-center justify-center shadow-lg`}>
                  <m.icon className="h-8 w-8 text-white" />
                </div>
                <span className="rounded-full bg-amber-50 px-3 py-1 text-sm font-bold text-amber-700">{m.badge}</span>
              </div>
              <h2 className="mt-6 font-heading text-2xl font-bold text-slate-900">{m.title}</h2>
              <p className="mt-2 text-base leading-relaxed text-slate-500">{m.desc}</p>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-400">{m.stat}</span>
                <span className="inline-flex items-center gap-2 text-base font-bold text-amber-600 group-hover:gap-3 transition-all">
                  {t("studentDash.open")} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Skills for candidate matching */}
        <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="student-skills-card">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-600" />
            <h3 className="font-heading text-xl font-bold text-slate-900">{t("studentDash.skillsTitle")}</h3>
          </div>
          <p className="mt-1 text-base text-slate-500">{t("studentDash.skillsSubtitle")}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {skills.length === 0 && <span className="text-sm text-slate-400">{t("studentDash.skillsEmpty")}</span>}
            {skills.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-700" data-testid="skill-chip">
                {s}
                <button onClick={() => removeSkill(s)} data-testid={`remove-skill-${s}`} className="hover:text-amber-900"><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 gap-2">
              <Input
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
                placeholder={t("studentDash.skillsPlaceholder")}
                data-testid="skill-input"
              />
              <Button type="button" variant="outline" onClick={addSkill} data-testid="add-skill-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <Button onClick={saveSkills} disabled={savingSkills} className="bg-amber-600 hover:bg-amber-700 font-bold" data-testid="save-skills-btn">
              {savingSkills ? "…" : t("studentDash.saveSkills")}
            </Button>
          </div>
        </div>

        <div className="mt-10">
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">{t("studentDash.quickLinks")}</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {quick.map((q) => (
              <button
                key={q.testid}
                onClick={() => navigate(q.to)}
                data-testid={q.testid}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-left transition-all hover:border-amber-300 hover:shadow-md"
              >
                <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <q.icon className="h-5 w-5 text-amber-600" />
                </div>
                <span className="text-base font-semibold text-slate-700">{q.label}</span>
              </button>
            ))}
          </div>
        </div>
      </main>
      <WhatsAppButton />
    </div>
  );
}
